import React, { createContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "../utils/supabaseClient";
import { useNavigate } from "react-router-dom";
import { setUserContext, clearUserContext, captureAuthError } from "../utils/sentry";

export interface AuthContextType {
  user: any | null;
  loading: boolean;
  signOut: () => Promise<void>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const defaultContext: AuthContextType = {
  user: null,
  loading: true,
  signOut: async () => {},
  setLoading: () => {},
};

export const AuthContext = createContext<AuthContextType>(defaultContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  useNavigate();

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data, error }) => {
        if (error) {
          captureAuthError(error, "get_session");
          setUser(null);
        } else {
          const sessionUser = data?.session?.user || null;
          setUser(sessionUser);
          // Set Sentry user context on initial load if user exists
          if (sessionUser) {
            setUserContext({
              id: sessionUser.id,
              email: sessionUser.email,
              role: sessionUser.user_metadata?.role,
              tenantId: sessionUser.user_metadata?.tenant_id,
            });
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        captureAuthError(err, "get_session_unexpected");
        setUser(null);
        setLoading(false);
      });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const currentUser = session?.user || null;
        setUser(currentUser);
        setLoading(false);

        // Set or clear Sentry user context based on auth state
        if (currentUser) {
          setUserContext({
            id: currentUser.id,
            email: currentUser.email,
            role: currentUser.user_metadata?.role,
            tenantId: currentUser.user_metadata?.tenant_id,
          });
        } else {
          clearUserContext();
        }
      }
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  // NOTE: there is deliberately no client-side signUp here. Account
  // creation goes through backend endpoints (membership registration,
  // customer creation, staff /auth/register) which own the auth +
  // profile-row writes (audit M5 / mobile backend-first). The old
  // signUp inserted a users row directly from the browser and was only
  // reachable from an unrouted Register page — both removed 2026-06-12.

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        throw error;
      }

      setUser(null);
      clearUserContext();
    } catch (error) {
      captureAuthError(error, "sign_out", user?.id);
      throw error;
    }
  };

  // There is no resetPassword here. Password reset goes through the backend
  // (the "Forgot password" page and the staff Reset Password action), which
  // builds a link for the right club. The copy that used to sit here asked the
  // browser's own database client for a link to /reset-password with no club
  // in it, and nothing in the app called it.

  const value = {
    user,
    loading,
    signOut,
    setLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
